import { useEffect, useRef, useState } from "react";

// Shared with ClampedTextTooltipManager rather than duplicated, so the two can never disagree about
// whether the same element is clipped.
import { CLAMP_PIXEL_THRESHOLD } from "@performyard/py-components";

/**
 * Reports whether an element's text is actually being clipped by CSS truncation, so a tooltip can
 * be shown only when it is.
 *
 * Elements clamped with the `py-` utility classes don't need this — `ClampedTextTooltipManager`
 * handles them app-wide. Reach for this only when opting out of that manager, e.g. to place the
 * tooltip somewhere other than its hardcoded `side="top"`.
 *
 * Temporary. Once the manager supports a per-element placement hint, its call sites can rejoin it
 * and this hook should be deleted:
 * @see https://performyard.atlassian.net/browse/PY-13806
 *
 * The ref must be attached to a **block-level** element. `ResizeObserver` never fires for a
 * non-replaced inline element, so an inline target would only ever be measured once, on mount.
 * Both current call sites use `class="block truncate"` for this reason.
 *
 * @param text the rendered text; re-measures when it changes
 */
export const useIsTextClipped = <T extends HTMLElement = HTMLSpanElement>(
	text?: string
): { ref: React.RefObject<T | null>; isClipped: boolean } => {
	const ref = useRef<T>(null);
	const [isClipped, setIsClipped] = useState(false);

	useEffect(() => {
		const element = ref.current;
		if (!element) {
			return;
		}
		const measure = () => setIsClipped(element.scrollWidth > element.clientWidth + CLAMP_PIXEL_THRESHOLD);
		measure();

		// Container resizes are already covered: the element is block-level, so its own box tracks
		// whatever width the container gives it. This covers the one case the observer cannot see —
		// mounting while the initial web-font load is still in flight, where the text's measured width
		// changes without any box changing. A font that begins loading later is still missed; that
		// would need a `loadingdone` subscription, which no site has needed.
		let cancelled = false;
		void document.fonts?.ready?.then(() => {
			if (!cancelled) {
				measure();
			}
		});

		const observer = new ResizeObserver(measure);
		observer.observe(element);
		return () => {
			cancelled = true;
			observer.disconnect();
		};
	}, [text]);

	return { ref, isClipped };
};
